import React, { useState } from 'react'
import {
  Container,
  Breadcrumb,
  Row,
  Col,
  Table,
  Button,
  Card,
  Form,
  InputGroup,
  Spinner,
  Alert,
} from 'react-bootstrap'
import { Link, useHistory } from 'react-router-dom'
import useTerminals from 'hooks/use-terminals'
import { Terminal, TerminalStatus } from 'interfaces/terminal'
import useDebounce from 'hooks/use-debounce'

const InventoryPage = () => {
  const history = useHistory()
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [page, setPage] = useState(1)

  const debouncedSearchTerm = useDebounce(searchTerm, 300)

  const {
    data: terminalsData,
    isLoading,
    error,
  } = useTerminals({
    page,
    limit: 20,
    search: debouncedSearchTerm,
    status: statusFilter,
  })

  const handleTerminalClick = (terminal: Terminal) => {
    history.push(`/inventory/terminals/${terminal.id}`)
  }

  const getStatusVariant = (status: TerminalStatus) => {
    switch (status) {
      case TerminalStatus.OPERATIONAL:
        return 'success'
      case TerminalStatus.MAINTENANCE:
        return 'warning'
      case TerminalStatus.OFFLINE:
        return 'danger'
      default:
        return 'secondary'
    }
  }

  if (error) {
    return (
      <Container>
        <Alert variant="danger">
          Error loading terminals:{' '}
          {error instanceof Error ? error.message : 'Unknown error'}
        </Alert>
      </Container>
    )
  }

  return (
    <Container>
      <Breadcrumb>
        <Breadcrumb.Item href="/">Home</Breadcrumb.Item>
        <Breadcrumb.Item active>Inventory</Breadcrumb.Item>
      </Breadcrumb>

      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1>Inventory Management</h1>
      </div>

      <Card className="mb-4">
        <Card.Header>
          <h4>Terminal Overview</h4>
        </Card.Header>
        <Card.Body>
          <Row className="mb-3">
            <Col md={6}>
              <InputGroup>
                <Form.Control
                  type="text"
                  placeholder="Search terminals..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>
            </Col>
            <Col md={4}>
              <Form.Control
                as="select"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="">All Statuses</option>
                <option value="OPERATIONAL">Operational</option>
                <option value="MAINTENANCE">Maintenance</option>
                <option value="OFFLINE">Offline</option>
              </Form.Control>
            </Col>
          </Row>

          {isLoading ? (
            <div className="text-center">
              <Spinner animation="border" />
              <div className="mt-2">Loading terminals...</div>
            </div>
          ) : (
            <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
              <Table striped bordered hover responsive variant="dark">
                <thead
                  style={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 100,
                    backgroundColor: '#343a40',
                  }}
                >
                  <tr>
                    <th style={{ borderTop: 'none', width: '80px' }}>#</th>
                    <th style={{ borderTop: 'none', width: '120px' }}>Name</th>
                    <th style={{ borderTop: 'none', width: '120px' }}>
                      Location
                    </th>
                    <th style={{ borderTop: 'none', width: '100px' }}>
                      Status
                    </th>
                    <th style={{ borderTop: 'none', width: '120px' }}>
                      Total Ansibles
                    </th>
                    <th style={{ borderTop: 'none', width: '120px' }}>
                      Active Ansibles
                    </th>
                    <th style={{ borderTop: 'none' }}>Address</th>
                    <th style={{ borderTop: 'none', width: '100px' }}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {terminalsData?.data?.map((terminal, index) => (
                    <tr
                      key={terminal.id}
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleTerminalClick(terminal)}
                    >
                      <td>{(page - 1) * 20 + index + 1}</td>
                      <td>
                        <Link
                          to={`/inventory/terminals/${terminal.id}`}
                          style={{
                            color: '#4da6ff',
                            textDecoration: 'underline',
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {terminal.name}
                        </Link>
                      </td>
                      <td>{terminal.locationCode}</td>
                      <td>
                        <span
                          className={`badge bg-${getStatusVariant(
                            terminal.status
                          )}`}
                        >
                          {terminal.status}
                        </span>
                      </td>
                      <td>{terminal.totalAnsibles}</td>
                      <td>{terminal.activeAnsibles}</td>
                      <td>{terminal.address || 'N/A'}</td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => handleTerminalClick(terminal)}
                        >
                          View Details
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}

          {terminalsData?.data?.length === 0 && (
            <div className="text-center text-muted py-4">
              No terminals found matching your criteria
            </div>
          )}
        </Card.Body>
      </Card>
    </Container>
  )
}

export default InventoryPage
